import { STATUSPAGE } from '@repo/ui';
import {
  DeleteUserUseCaseInterface,
  CreateEvaluationCriteriaUseCaseInterface,
  GetEvaluationCriteriaByIdUseCaseInterface,
  UpdateEvaluationCriteriaUseCaseInterface,
  CloneEvaluationCriteriaUseCaseInterface,
} from 'domain/usecases';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadEntitiesListData } from 'presentation/config/store/entities/entities-slice';
import {
  loadEvaluationCriterias,
  clearEvaluationCriterias,
  loadSelectedEvaluationCriteria,
  clearSelectedEvaluationCriteria,
} from 'presentation/config/store/evaluation-criteria/evaluation-criteria-slice';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export const useEvaluationCriteria = () => {
  // States
  const [getAllStatus, setGetAllStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [createOrUpdateStatus, setCreateOrUpdateStatus] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [getByIdStatus, setGetByIdStatus] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [cloneStatus, setCloneStatus] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  const { evaluationCriterias, selectedEvaluationCriteria } = useAppSelector(
    (state) => state.EvaluationCriteria,
  );

  // Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { evaluationCriteriaService, entitiesService } = useService();
  const { entitiesListData } = useAppSelector((state) => state.Entities);

  // Constants
  const areInputsAndBtnsDisabled =
    getByIdStatus === STATUSPAGE.LOADING ||
    createOrUpdateStatus === STATUSPAGE.LOADING;

  // Methods
  const requestClearSelectedEvaluationCriteria = useCallback(() => {
    dispatch(clearSelectedEvaluationCriteria());
  }, []);

  const requestGetAllEvaluationCriteria = useCallback(async () => {
    setGetAllStatus(STATUSPAGE.LOADING);
    await evaluationCriteriaService
      .getAllEvaluationCriteria()
      .then((res) => {
        dispatch(loadEvaluationCriterias(res));
      })
      .catch((res) => {
        notifyError(res);
        dispatch(clearEvaluationCriterias());
      })
      .finally(() => {
        setGetAllStatus(STATUSPAGE.DONE);
      });
  }, []);

  const requestCopyEvaluationCriteria = useCallback(
    async (
      params: CloneEvaluationCriteriaUseCaseInterface.Params,
      toggleModalCopyCriteria: () => void,
    ) => {
      setCloneStatus(STATUSPAGE.LOADING);
      await evaluationCriteriaService
        .cloneEvaluationCriteria(params)
        .then((res) => {
          notifySuccess(res.message);
          toggleModalCopyCriteria();
          requestGetAllEvaluationCriteria();
          navigate(`/cadastros/instrumentos-de-avaliacao/editar/${res.id}`);
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setCloneStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestCreateEvaluationCriteria = useCallback(
    async (params: CreateEvaluationCriteriaUseCaseInterface.Params) => {
      setCreateOrUpdateStatus(STATUSPAGE.LOADING);
      await evaluationCriteriaService
        .createEvaluationCriteria(params)
        .then((res) => {
          notifySuccess(res.message);
          navigate(`/cadastros/instrumentos-de-avaliacao/editar/${res.id}`);
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setCreateOrUpdateStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetEvaluationCriteriaById = useCallback(
    async (params: GetEvaluationCriteriaByIdUseCaseInterface.Params) => {
      setGetByIdStatus(STATUSPAGE.LOADING);
      await evaluationCriteriaService
        .getEvaluationCriteriaById(params)
        .then((res) => {
          dispatch(loadSelectedEvaluationCriteria(res));
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setGetByIdStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestUpdateEvaluationCriteria = useCallback(
    async (params: UpdateEvaluationCriteriaUseCaseInterface.Params) => {
      setCreateOrUpdateStatus(STATUSPAGE.LOADING);
      await evaluationCriteriaService
        .updateEvaluationCriteria(params)
        .then((res) => {
          notifySuccess(res.message);
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setCreateOrUpdateStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestDeleteEvaluationCriteria = useCallback(
    async (params: DeleteUserUseCaseInterface.Params) => {
      await evaluationCriteriaService
        .deleteEvaluationCriteria(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllEvaluationCriteria();
        })
        .catch((err) => {
          notifyError(err.message || 'Não foi possível excluir o instrumento de avaliação');
        });
    },
    [],
  );

  const requestGetAllEntities = useCallback(async () => {
    return entitiesService
      .getAllEntities()
      .then((res) => {
        dispatch(loadEntitiesListData(res));
      })
      .catch((err) => {
        notifyError(err.message);
      });
  }, []);

  return {
    // states & data
    navigate,
    cloneStatus,
    getAllStatus,
    getByIdStatus,
    entitiesListData,
    createOrUpdateStatus,
    areInputsAndBtnsDisabled,
    evaluationCriterias,
    selectedEvaluationCriteria,

    // methods
    requestGetAllEntities,
    requestCopyEvaluationCriteria,
    requestCreateEvaluationCriteria,
    requestGetAllEvaluationCriteria,
    requestDeleteEvaluationCriteria,
    requestUpdateEvaluationCriteria,
    requestGetEvaluationCriteriaById,
    requestClearSelectedEvaluationCriteria,
  };
};
